import { useMutation, useQuery } from "@tanstack/react-query";
import { Api, AttachmentMetaDto } from "../../api/Api";
import { getAuthenticatedApiClient } from "../../api/apiClient";
import {
  downloadAttachment,
  uploadAttachment
} from "../../api/attachmentTransfer";
import {
  AttachmentMutationCallbacks,
  AttachmentRef,
  LifecycleAttachmentApi
} from "./types";

type AttachmentScope = Omit<AttachmentRef, "fileName">;

type AttachmentEndpoints = {
  listKey: LifecycleAttachmentApi["listKey"];
  list: (
    client: Api<unknown>,
    scope: AttachmentScope
  ) => Promise<{ data: AttachmentMetaDto[] }>;
  download: (
    client: Api<unknown>,
    ref: AttachmentRef
  ) => Promise<{ data: Blob }>;
  delete: (client: Api<unknown>, ref: AttachmentRef) => Promise<unknown>;
  upload: { path: string; idParameter: string };
};

export const createAttachmentApi = (
  endpoints: AttachmentEndpoints
): LifecycleAttachmentApi => ({
  listKey: endpoints.listKey,
  useList: (entityId, lifecycleName) =>
    useQuery({
      queryKey: endpoints.listKey(entityId, lifecycleName),
      queryFn: async () => {
        const client = await getAuthenticatedApiClient();
        const response = await endpoints.list(client, {
          entityId,
          lifecycleName
        });
        return response.data;
      },
      enabled: !!entityId && !!lifecycleName
    }),
  useUpload: () =>
    useMutation({
      mutationFn: async ({
        entityId,
        lifecycleName,
        file
      }: AttachmentScope & { file: File }) => {
        const client = await getAuthenticatedApiClient();
        return uploadAttachment(
          client,
          endpoints.upload.path,
          { [endpoints.upload.idParameter]: entityId, lifecycleName },
          file
        );
      }
    }),
  useDownload: () =>
    useMutation({
      mutationFn: async (ref: AttachmentRef) => {
        const client = await getAuthenticatedApiClient();
        const response = await endpoints.download(client, ref);
        downloadAttachment(response.data, ref.fileName);
      }
    }),
  useDelete: (options?: AttachmentMutationCallbacks) =>
    useMutation({
      mutationFn: async (ref: AttachmentRef) => {
        const client = await getAuthenticatedApiClient();
        await endpoints.delete(client, ref);
      },
      onSuccess: () => options?.onSuccess?.(),
      onError: (error: Error) => options?.onError?.(error)
    })
});
